<template>
  <div>
    <p class="title mb-2" :class="titleClass ?? ''" v-if="title">{{ title }}</p>
    <div class="code-input">
      <input
        v-for="(v, index) in values"
        :key="index"
        class="form-control w-14 h-14 rounded-lg text-center"
        :style="{
          width: `${fieldWidth}px`,
          height: `${fieldHeight}px`,
        }"
        :autoFocus="autoFocus && index === autoFocusIndex"
        :data-id="index"
        :value="v"
        :ref="
          (el) => {
            if (el) {
              inputs[index + 1] = el;
            }
          }
        "
        @input="onValueChange"
        @focus="onFocus"
        @keydown="onKeyDown"
        :disabled="disabled"
      />
    </div>
    <span v-if="!validated" :class="erroMessageClass">{{ errorMessage }}</span>
  </div>
</template>

<script>
// import { ref } from 'vue';
const KEY_CODE = {
  backspace: 8,
  delete: 46,
  left: 37,
  up: 38,
  right: 39,
  down: 40,
};

export default {
  props: {
    className: String,
    fields: {
      type: Number,
      default: 3,
    },
    fieldWidth: {
      type: Number,
      default: 56,
    },
    fieldHeight: {
      type: Number,
      default: 56,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: true,
    },
    title: {
      type: String,
      required: false,
      default: '',
    },
    titleClass: {
      type: String,
      required: false,
      default: '',
    },
    errorMessage: {
      type: String,
      required: false,
      default: '',
    },
    erroMessageClass: {
      type: String,
      required: false,
      default: 'text-danger',
    },
  },
  data() {
    return {
      values: [],
      iRefs: [],
      inputs: [],

      autoFocusIndex: 0,
      autoFocus: true,
      validated: true,
    };
  },
  methods: {
    initVals() {
      let vals;
      if (this.values && this.values.length) {
        vals = [];
        for (let i = 0; i < this.fields; i++) {
          vals.push(this.values[i] || '');
        }
      } else {
        vals = Array(this.fields).fill('');
      }
      this.iRefs = [];
      for (let i = 0; i < this.fields; i++) {
        this.iRefs.push(i + 1);
      }
      this.values = vals;

      this.autoFocusIndex = 0;
    },
    setFocus() {
      this.inputs[1]?.focus();
    },
    showErrorMessage() {
      this.validated = false;
    },
    setValue(val) {
      if (this.inputs[1]) {
        this.inputs[1].value = val.replace(/[^\d]/gi, '');
        this.inputs[1].dispatchEvent(new Event('input', { bubbles: true }));
      }
    },
    onFocus(e) {
      e.target.select(e);
    },
    onValueChange(e) {
      const index = parseInt(e.target.dataset.id);
      e.target.value = e.target.value.replace(/[^\d]/gi, '');
      // this.handleKeys[index] = false;
      if (e.target.value === '' || !e.target.validity.valid) {
        return;
      }
      let next;
      const value = e.target.value;
      this.values = Object.assign([], this.values);
      if (value.length > 1) {
        let nextIndex = value.length + index - 1;
        if (nextIndex >= this.fields) {
          nextIndex = this.fields - 1;
        }
        next = this.iRefs[nextIndex];
        const split = value.split('');
        split.forEach((item, i) => {
          const cursor = index + i;
          if (cursor < this.fields) {
            this.values[cursor] = item;
          }
        });
      } else {
        next = this.iRefs[index + 1];
        this.values[index] = value;
      }
      if (next) {
        const element = this.inputs[next];
        element.focus();
        element.select();
      }
      this.triggerChange(this.values);
    },
    onKeyDown(e) {
      const index = parseInt(e.target.dataset.id);
      const prevIndex = index - 1;
      const nextIndex = index + 1;
      const prev = this.iRefs[prevIndex];
      const next = this.iRefs[nextIndex];
      switch (e.keyCode) {
        case KEY_CODE.backspace: {
          e.preventDefault();
          const vals = [...this.values];
          if (this.values[index]) {
            vals[index] = '';
            this.values = vals;
            this.triggerChange(vals);
          } else if (prev) {
            vals[prevIndex] = '';
            this.inputs[prev].focus();
            this.values = vals;
            this.triggerChange(vals);
          }
          break;
        }
        case KEY_CODE.delete: {
          e.preventDefault();
          const vals = [...this.values];
          if (this.values[index]) {
            vals[index] = '';
            this.values = vals;
            this.triggerChange(vals);
          } else if (next) {
            vals[nextIndex] = '';
            this.inputs[next].focus();
            this.values = vals;
            this.triggerChange(vals);
          }
          break;
        }
        case KEY_CODE.left:
          e.preventDefault();
          if (prev) {
            this.inputs[prev].focus();
          }
          break;
        case KEY_CODE.right:
          e.preventDefault();
          if (next) {
            this.inputs[next].focus();
          }
          break;
        case KEY_CODE.up:
        case KEY_CODE.down:
          e.preventDefault();
          break;
        default:
          // this.handleKeys[index] = true;
          break;
      }
    },
    triggerChange(values) {
      const val = values.join('');
      this.$emit('change', val);
      this.$emit('complete', val.length >= this.fields, val);
    },
  },

  onBeforeUpdate() {
    this.inputs = [];
  },
  mounted() {
    this.initVals();
  },
};
</script>

<style scoped>
.code-input-container {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 20px;
}
.code-input {
  display: flex;
  flex-direction: row;
  justify-content: center;
  gap: 10px;
}
.code-input > input {
  border: solid 1px #a8adb7;
  padding: 0px !important;
  border-radius: 8px;
  text-align: center;
  transition: 0.2s all ease-in-out;
  color: #525461;
  box-sizing: border-box;
  -webkit-appearance: initial;
}
.code-input > input:focus {
  color: #1c0950;
  background-color: #ffffff;
  border-color: rgba(87, 79, 236, 0.5);
  outline: 0;
  box-shadow: 0 0 25px rgba(87, 79, 236, 0.1);
}
.title {
  margin: 0;
  height: 20px;
  padding-bottom: 10px;
}
</style>
